'use client'

import React, { useEffect, useRef } from 'react'
import { FaUserCircle, FaCalendarAlt, FaClock, FaArrowLeft, FaArrowRight } from 'react-icons/fa'
import Link from 'next/link'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { blogPosts, getRelatedPosts, getPostBySlug, getAuthorForPost, type BlogPostMeta } from '@/app/blog/posts'
import { readNextPosts } from '@/app/blog/readNext'
import { clusterHubFor } from '@/app/blog/clusters'
import Breadcrumbs from '@/app/components/Breadcrumbs'
import AppStoreLink from '@/app/components/AppStoreLink'
import StickyMobileAppCTA from '@/app/components/StickyMobileAppCTA'
import MidArticleCTA from '@/app/components/blog/MidArticleCTA'
import ReadNextInline from '@/app/components/blog/ReadNextInline'
import { readNextInsertIndex } from '@/app/components/blog/readNextPlacement'
import HeroAnswer from '@/app/components/blog/HeroAnswer'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'
import AppCtaTrustRow from '@/app/components/blog/AppCtaTrustRow'
import AndroidWaitlistCard from '@/app/components/AndroidWaitlistCard'
import { BlogAppCTA, BlogComparisonTable, BlogKeyTakeaways, BlogTLDR } from '@/app/components/blog/BlogAeo'
import ResearchSupportCard from '@/app/components/ResearchSupportCard'
import ShareButtons from '@/app/components/ShareButtons'
import FirstScanCallout from '@/app/components/FirstScanCallout'
import IngredientCheckerPromo from '@/app/components/IngredientCheckerPromo'
import { getFirstScanPrompt } from '@/app/blog/firstScanPrompts'
import { getEditorialLinks } from '@/app/blog/editorialLinks'
import { getBlogHeroImage } from '@/app/blog/heroImages'
import { track } from '@/utils/analytics'
import PreferredSourcesArticleCard from '@/app/components/blog/PreferredSourcesArticleCard'
import ArticlePortrait from '@/app/components/blog/ArticlePortrait'
import { ARTICLE_PORTRAITS } from '@/app/blog/articlePortraits'

// ─────────────────────────────────────────────────────────────────────────────
// Topic-aware bottom-of-article CTA. Stable source labels let analytics
// compare each surface. Copy changes are hypotheses until measured after release.
// ─────────────────────────────────────────────────────────────────────────────
type Topic = NonNullable<BlogPostMeta['topic']>

const TOPIC_BOTTOM_CTA: Record<Topic, { headline: string; sub: string; source: string }> = {
  water: {
    headline: 'Track every water source you actually drink',
    sub: 'Save bottled brands, filtered tap sources, and travel-water situations to compare a modeled packaging-contact pattern over time. It is not a personal exposure profile.',
    source: 'blog_bottom_cta_water',
  },
  food: {
    headline: 'Compare what your food and drink touch',
    sub: 'Save products, review declared materials and compare modeled contact inputs. Scans do not measure particles in food or beverages.',
    source: 'blog_bottom_cta_food',
  },
  cosmetics: {
    headline: 'Build your cosmetics ingredient watchlist',
    sub: 'Organize product information and review declared ingredient names. The web checker handles one label at a time; name matches do not measure particles or certify a formula.',
    source: 'blog_bottom_cta_cosmetics',
  },
  pregnancy: {
    headline: 'Build your nursery + pregnancy product audit',
    sub: 'Save baby-product assessments and get an alert when a saved product gets new research. The app remembers the product context; it does not measure pregnancy or infant exposure.',
    source: 'blog_bottom_cta_pregnancy',
  },
  home: {
    headline: 'Compare your kitchen contact pattern over time',
    sub: 'The app turns saved scans into a modeled household contact pattern based on what you cooked in, stored in, and heated this month. The trend describes the assessed mix, not health or bodily exposure.',
    source: 'blog_bottom_cta_home',
  },
  clothing: {
    headline: 'Build a closet audit for microfiber-shed',
    sub: 'The app records declared fiber types and laundry-product assessments so future comparisons can reflect what you own.',
    source: 'blog_bottom_cta_clothing',
  },
  health: {
    headline: 'Turn this article into a saved-scan baseline',
    sub: 'Review products you use often. The app saves a modeled packaging-contact baseline and shows how that assessed mix changes month over month—not what is in your body.',
    source: 'blog_bottom_cta_health',
  },
  general: {
    headline: 'Turn one-time scans into a contact-pattern trend line',
    sub: 'The web shows the research; the app remembers saved scans and compares how the modeled packaging-contact pattern changes month over month. View current subscription terms in the App Store.',
    source: 'blog_bottom_cta_general',
  },
}

// ─────────────────────────────────────────────────────────────────────────────
// Topic → primary hub. Used both for the "New here?" aside AND for the
// intermediate breadcrumb crumb. Switching the breadcrumb from a generic
// "Articles" crumb to the topic-relevant hub passes topical link equity
// through the breadcrumb structure and helps Google understand the post's
// home cluster.
//
// Posts with no clean hub match fall back to /blog (the chronological feed).
// ─────────────────────────────────────────────────────────────────────────────
const TOPIC_HUB: Record<Topic, { href: string; label: string }> = {
  water: { href: '/microplastic-free-products', label: 'Lower-Contact Product Guides' },
  food: { href: '/microplastic-free-products', label: 'Lower-Contact Product Guides' },
  cosmetics: { href: '/microplastic-free-products', label: 'Lower-Contact Product Guides' },
  home: { href: '/microplastic-free-products', label: 'Lower-Contact Product Guides' },
  clothing: { href: '/microplastic-free-products', label: 'Lower-Contact Product Guides' },
  pregnancy: { href: '/microplastics-pregnancy', label: 'Pregnancy & Baby Hub' },
  health: { href: '/microplastics-research', label: 'Research Hub' },
  general: { href: '/microplastics-guide', label: 'Microplastics Guide' },
}

// Old placeholder hero — articles that pass any of these should fall through to
// the topic-aware hero mapping above. Covers every legacy app-screenshot file
// that was used as a generic placeholder across earlier batches.
const PLACEHOLDER_HEROES = new Set([
  '/blog/app-tracking.png',
  '/blog/app-home.png',
  '/blog/app-search.png',
  '/blog/app-profile.png',
  // The eight topic images are placeholders too (added 2026-08-10). A post that
  // passes `heroImage="/blog/heroes/food.webp"` is not choosing that picture,
  // it is declining to choose one — same intent as the app-screenshot
  // placeholders above. Treating them as explicit was blocking ARTICLE_HEROES
  // from ever applying to the ~94 posts that now have a subject-matched hero.
  //
  // Safe by construction: a post with no ARTICLE_HEROES entry falls through to
  // getBlogHeroImage, which returns TOPIC_HEROES[topic] — the same file it was
  // passing in by hand. Output only changes where a better image now exists.
  '/blog/heroes/water.webp',
  '/blog/heroes/food.webp',
  '/blog/heroes/baby.webp',
  '/blog/heroes/home.webp',
  '/blog/heroes/health.webp',
  '/blog/heroes/cosmetics.webp',
  '/blog/heroes/clothing.webp',
  '/blog/heroes/general.webp',
])

// ─────────────────────────────────────────────────────────────────────────────
// Auto-inject a topic-aware MidArticleCTA at the ~30% mark of every post.
//
// Why: ~120 of ~262 PostContent files have no inline App Store CTA at all,
// and even the ones that do place CTAs only at the bottom. Mobile readers
// drop off long before they reach the bottom. A layout-level injection
// gives every post (including future ones) a mid-funnel install ask
// without per-post edits.
//
// Behaviour:
//  - Skip if the post body has fewer than 5 top-level children — short
//    posts don't have room for a mid-article ask.
//  - Skip if the post body already contains a BlogAppCTA or MidArticleCTA
//    instance (compared by component reference, so it survives prod
//    minification of function names). Authors who have manually placed an
//    inline CTA keep their own placement; we don't double up.
//  - Insert at ~18% of children, never before index 2 (so we never land
//    before the intro paragraph or TLDR block), and never after index 6.
//
// On the placement fraction (was 0.3, now 0.18 + a hard cap):
// GA4 scroll data for the 22 days to 2026-07-28 — 5,860 article pageviews →
// 2,243 reached 25% (38%) → 1,273 reached 50% (22%) → 640 reached 75% (11%)
// → 474 reached 90% (8%). The median reader never passes ~35-40% of the page,
// so anything below the 50% mark is seen by roughly 1 reader in 5.
//
// Element-count fractions are NOT scroll-depth fractions, and the gap widens
// with post length. Body elements vary from one-line paragraphs to 20-row
// comparison tables, and the body is not the whole document: the hoisted
// HeroAnswer card, a 400px hero image and the TOC all sit above it, while the
// bottom CTA, share buttons, related posts and footer sit below. On a 60-child
// post, 30% of children landed well past 40% of document pixels — i.e. past
// the point most readers had already left.
// The cap is what actually enforces the pixel bound: a fixed ceiling of 6 body
// children keeps the ask early in pixel terms no matter how long the post runs,
// which the fraction alone cannot guarantee.
//
// Placing it this early is also fine on its own terms: HeroAnswer already gives
// the reader the article's verdict above the fold, so the ask has been earned
// by the time they hit the third or fourth block.
// ─────────────────────────────────────────────────────────────────────────────
function containsComparisonTable(node: React.ReactNode): boolean {
  return React.Children.toArray(node).some((child) => {
    if (!React.isValidElement<{ children?: React.ReactNode }>(child)) return false
    if (child.type === BlogComparisonTable || child.type === 'table') return true
    return (typeof child.type === 'string' || child.type === React.Fragment)
      && containsComparisonTable(child.props.children)
  })
}

function injectResearchSupport(children: React.ReactNode, postSlug: string, postTopic: string): React.ReactNode[] {
  const elements = React.Children.toArray(children)
  const comparisonIndex = elements.findIndex(containsComparisonTable)
  const takeawayIndex = elements.findIndex((child) => React.isValidElement(child) && child.type === BlogKeyTakeaways)
  const surface = comparisonIndex >= 0 ? 'article_comparison' : takeawayIndex >= 0 ? 'article_takeaways' : 'article_end'

  // Keep the comparison's explanatory paragraphs and source notes together.
  // A single placement per article also avoids an ask after every table.
  let insertAt = comparisonIndex >= 0 ? comparisonIndex + 1 : takeawayIndex >= 0 ? takeawayIndex + 1 : elements.length
  let next = elements[insertAt]
  while (React.isValidElement(next) && next.type === 'p') {
    next = elements[++insertAt]
  }

  return [
    ...elements.slice(0, insertAt),
    <ResearchSupportCard key="__mp_research_support" surface={surface} postSlug={postSlug} postTopic={postTopic} />,
    ...elements.slice(insertAt),
  ]
}

function shouldSkipInjection(elements: React.ReactNode[]): boolean {
  if (elements.length < 5) return true
  // Only skip when an author placed a manual CTA in the FIRST HALF of the
  // post — that's the only case where auto-injection would double up nearby.
  // The old check skipped on a CTA existing ANYWHERE, and since 176 of 177
  // posts have a BlogAppCTA near the bottom, the "~30% mark install ask"
  // never actually rendered anywhere on the site (July 2026 funnel audit).
  const midpoint = Math.floor(elements.length / 2)
  return elements.some((child, i) => {
    if (i > midpoint) return false
    if (!React.isValidElement(child)) return false
    // Reference equality on the component type — works in prod because both
    // sides import from the same module instance.
    return child.type === BlogAppCTA || child.type === MidArticleCTA
  })
}

function injectMidArticleCTA(
  children: React.ReactNode,
  topic: NonNullable<BlogPostMeta['topic']>,
  postSlug: string
): React.ReactNode[] {
  const elements = React.Children.toArray(children)
  if (shouldSkipInjection(elements)) return elements

  // 2026-08-03 funnel audit. GA4 (28 days) measured the reach directly:
  // hero_answer_impression fires on mount, so it counts every real article
  // render — 1,743. mid_article_cta_impression fires on IntersectionObserver,
  // so it counts people who actually scrolled to it — 838. That is a reach of
  // 48%: more than half of readers never saw the install ask at all.
  //
  // Per-impression the two surfaces convert almost identically (hero answer
  // 26/1,743 = 1.49%, mid CTA 14/838 = 1.67% attributed app_store_clicks via
  // cta_surface), so the mid CTA is not underperforming — it is under-seen.
  // Reach is the whole lever, and the fix is to sit above the drop-off rather
  // than below it: only 37.5% of pageviews reach scroll_25 at all.
  //
  // 0.18 with a cap of 6 put it after the first h2 and its opening paragraph
  // on a typical 40-child post. 0.10 with a cap of 5 and a floor of 3 puts it
  // directly after the TL;DR and key-takeaways block, which is where the
  // reader has just been given the verdict and is deciding whether to keep
  // reading — the highest-intent moment on the page, and above where most of
  // the abandonment happens. The floor of 3 still guarantees it never lands
  // before the TL;DR, so the answer-first structure is preserved.
  const insertAt = Math.min(5, Math.max(3, Math.floor(elements.length * 0.10)))
  return [
    ...elements.slice(0, insertAt),
    <MidArticleCTA key="__mp_auto_mid_cta" topic={topic} postSlug={postSlug} />,
    ...elements.slice(insertAt),
  ]
}

// Inline "Read next" after the mid-article CTA; placement rules and the scroll
// data behind them are in readNextPlacement.ts.
function injectReadNext(
  children: React.ReactNode,
  posts: BlogPostMeta[],
  hub: { href: string; label: string },
  postSlug: string,
  postTopic: string
): React.ReactNode[] {
  const elements = React.Children.toArray(children)
  if (posts.length === 0) return elements
  const kinds = elements.map((child) => (React.isValidElement(child) && child.type === 'h2' ? 'h2' : 'other') as 'h2' | 'other')
  const ctaIndex = elements.findIndex((child) => React.isValidElement(child) && child.type === MidArticleCTA)
  const insertAt = readNextInsertIndex(kinds, ctaIndex)
  if (insertAt === null) return elements
  return [
    ...elements.slice(0, insertAt),
    <ReadNextInline key="__mp_read_next" posts={posts} hub={hub} postSlug={postSlug} postTopic={postTopic} />,
    ...elements.slice(insertAt),
  ]
}

// ─────────────────────────────────────────────────────────────────────────────
// ---------------------------------------------------------------------------
// Auto table of contents.
//
// Long-form posts (10-15 min reads) benefit from an "On this page" nav in
// three measurable ways: Google can render fragment "jump to" links in the
// SERP, readers reach the section that answers their query without
// scroll-hunting, and AI answer engines cite anchored sections directly.
// None of the ~185 article bodies define heading ids, so the layout builds
// them: it walks the article's direct children, slugifies each h2's text
// into an id (h2s are direct children in the house article style), and
// renders the nav server-side so the anchors exist in the static HTML.
// Posts with fewer than 3 h2s get no TOC — a 2-item TOC is noise.
// ---------------------------------------------------------------------------
function textOfNode(node: React.ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(textOfNode).join('')
  // The generic is required under React 19's types: isValidElement now narrows
  // props to `unknown` rather than `any`, so an untyped call cannot read
  // .children. Naming the shape we actually read keeps this type-safe.
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
    return textOfNode(node.props.children)
  }
  return ''
}

function slugifyHeading(text: string, taken: Set<string>): string {
  const base =
    text
      .toLowerCase()
      .replace(/['\u2019]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'section'
  let id = base
  let n = 2
  while (taken.has(id)) id = `${base}-${n++}`
  taken.add(id)
  return id
}

function buildToc(children: React.ReactNode): {
  toc: Array<{ id: string; text: string }>
  body: React.ReactNode
} {
  const elements = React.Children.toArray(children)
  const toc: Array<{ id: string; text: string }> = []
  const taken = new Set<string>()

  const body = elements.map((child) => {
    // Generic required under React 19 types — see textOfNode above.
    if (
      !React.isValidElement<{ children?: React.ReactNode; id?: string; className?: string }>(child) ||
      child.type !== 'h2'
    ) {
      return child
    }
    const text = textOfNode(child.props.children).trim()
    if (!text) return child
    const id = child.props.id ?? slugifyHeading(text, taken)
    toc.push({ id, text })
    // Direct utility: prose-headings variants require the absent typography plugin.
    // Keep native fragment navigation clear of the fixed site header.
    return React.cloneElement(child, {
      id,
      className: [
        child.props.className || 'font-display text-2xl font-bold text-ink mt-8 mb-4',
        'scroll-mt-24',
      ].join(' '),
    })
  })

  if (toc.length < 3) return { toc: [], body: children }
  return { toc, body }
}

// Hoist the article's BlogTLDR verdict above the hero image.
//
// Every post already opens with a BlogTLDR that answers the search query in a
// sentence — but it rendered *below* a 400px hero image, which pushed it out of
// the first viewport on most phones. So the reader's own question was answered
// on screen two, and screen one was an app banner (see HeroAnswer for why that
// banner is gone: 2,975 impressions → 11 clicks → 54 dismissals).
//
// Pulling the TLDR up costs nothing, requires no edits to any of the 185
// article files, and puts the answer where both the reader and Google look
// first. The hero image simply moves below it.
//
// Reference equality on the component type is the same trick shouldSkipInjection
// uses, and it survives prod minification because both sides import from the
// same module instance.
// ─────────────────────────────────────────────────────────────────────────────
function extractTLDR(children: React.ReactNode): {
  tldr: React.ReactNode | null
  rest: React.ReactNode[]
} {
  const elements = React.Children.toArray(children)
  const index = elements.findIndex(
    (child) => React.isValidElement(child) && child.type === BlogTLDR
  )
  // Posts written before the TLDR convention (or any that simply omit it) fall
  // through with tldr: null. They get no hero card at all — which is still
  // strictly better than the banner they had, since that banner was net
  // -negative on its own numbers.
  if (index === -1) return { tldr: null, rest: elements }

  return {
    tldr: elements[index],
    rest: [...elements.slice(0, index), ...elements.slice(index + 1)],
  }
}

interface BlogPostLayoutProps {
  title: string
  author: string
  date: string
  readTime: string
  heroImage?: string
  heroImageAlt?: string
  heroImageCaption?: string
  /** Curated follow-ups when a source review needs narrower related coverage. */
  relatedSlugs?: string[]
  children: React.ReactNode
}

export default function BlogPostLayout({
  title,
  author,
  date,
  readTime,
  heroImage,
  heroImageAlt,
  heroImageCaption,
  relatedSlugs,
  children,
}: BlogPostLayoutProps) {
  const pathname = usePathname() || ''
  const slug = pathname.split('/').filter(Boolean).pop() || ''
  // A curated set can avoid promoting unreviewed conclusions alongside a
  // correction. Other pages retain their existing topical suggestions.
  const related = relatedSlugs
    ? Array.from(new Set(relatedSlugs)).filter(candidate => candidate !== slug)
      .map(getPostBySlug).filter((candidate): candidate is BlogPostMeta => candidate !== undefined)
    : slug ? getRelatedPosts(slug, 6) : []
  const editorial = slug ? getEditorialLinks(slug) : []
  const post = slug ? getPostBySlug(slug) : undefined

  // Resolve topic-dependent values once. Falls back to `general` for any post
  // that hasn't tagged a topic (shouldn't happen — `topic` is required in
  // BlogPostMeta — but defensive in case posts.ts ever loosens that).
  const topic: Topic = post?.topic ?? 'general'
  const hub = TOPIC_HUB[topic]

  // Inline Read next: the two most relevant same-topic posts, topped up from
  // the related ring when fewer clear the bar, and the post's own cluster hub
  // (coffee, water bottles) ahead of the broad topic hub. See readNext.ts.
  const readNext = (() => {
    const picked = slug ? readNextPosts(slug, blogPosts, 2) : []
    for (const p of related) {
      if (picked.length >= 2) break
      if (!picked.some((q) => q.slug === p.slug)) picked.push(p)
    }
    return picked
  })()
  const readNextHub = (slug && clusterHubFor(slug)) || hub
  const bottomCta = TOPIC_BOTTOM_CTA[topic]

  // Use the article-specific product-information task where available.
  // Conversion and activation effects still require release measurement.
  const firstScanPrompt = slug ? getFirstScanPrompt(slug) : undefined

  // ───────────────────────────────────────────────────────────────────────
  // Scroll-depth tracking. Fires `scroll_25 / 50 / 75 / 90 _article` once
  // each per page load — never duplicated. These aren't key events but they
  // surface in GA4 Explore as engagement signals, and the 50→90 funnel is
  // a strong leading indicator of "ready to install" intent.
  //
  // Implementation:
  //  - Single passive scroll listener.
  //  - Set tracks which thresholds have already fired so we never
  //    double-count.
  //  - Listener detaches once all four thresholds have been hit.
  // ───────────────────────────────────────────────────────────────────────
  const firedScrollDepthsRef = useRef<Set<number>>(new Set())
  useEffect(() => {
    if (!slug) return
    const thresholds: Array<{ pct: number; name: 'scroll_25_article' | 'scroll_50_article' | 'scroll_75_article' | 'scroll_90_article' }> = [
      { pct: 0.25, name: 'scroll_25_article' },
      { pct: 0.5, name: 'scroll_50_article' },
      { pct: 0.75, name: 'scroll_75_article' },
      { pct: 0.9, name: 'scroll_90_article' },
    ]
    const fired = firedScrollDepthsRef.current
    const onScroll = () => {
      const docHeight = document.documentElement.scrollHeight - window.innerHeight
      if (docHeight <= 0) return
      const pct = window.scrollY / docHeight
      for (const t of thresholds) {
        if (pct >= t.pct && !fired.has(t.pct)) {
          fired.add(t.pct)
          track(t.name, {
            post_slug: slug,
            post_topic: topic,
            page_type: 'blog',
          })
        }
      }
      if (fired.size === thresholds.length) {
        window.removeEventListener('scroll', onScroll)
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [slug, topic])

  // Auto-resolve the hero from the article's topic when no real hero was provided
  // (or when a known placeholder was passed). Keeps 100+ article files untouched.
  const resolvedHero = (() => {
    if (heroImage && !PLACEHOLDER_HEROES.has(heroImage)) return heroImage
    if (post?.topic) return getBlogHeroImage(post)
    return heroImage // fall back to whatever was passed (or undefined)
  })()

  // Auto-resolve the named editorial-team author from the article's topic so
  // the byline shows the real editor (with headshot + profile link) instead of
  // the legacy "MicroPlastics Editorial Team" placeholder. Zero article edits.
  const resolvedAuthor = post ? getAuthorForPost(post) : undefined

  // Lift the article's TLDR verdict out of the body so it can render above the
  // hero image, in the first viewport. `bodyChildren` is the article minus that
  // block, so the verdict isn't printed twice.
  const { tldr: heroTLDR, rest: bodyChildren } = extractTLDR(children)
  const { toc, body: tocBody } = buildToc(bodyChildren)

  // A review date that predates publication is not a trust signal, it is a
  // visible contradiction — and on a YMYL health page it undermines the exact
  // thing it exists to support. `does-fairlife-have-microplastics` shipped with
  // `modifiedISO` 2026-07-16 under a 2026-07-17 publish date.
  //
  // Clamping here rather than correcting one row fixes it for every post,
  // including any future row that goes in backwards: the reviewed date is the
  // later of the two, and disappears entirely if we only have a publish date.
  const reviewedISO =
    post?.modifiedISO && post?.publishedISO
      ? (new Date(post.modifiedISO) < new Date(post.publishedISO)
          ? post.publishedISO
          : post.modifiedISO)
      : post?.modifiedISO

  return (
    <div className="min-h-screen bg-white">
      <div className="content py-8 sm:py-12">
        <div className="w-full">
          {/* Shared Breadcrumbs component — same visual style + JSON-LD as
              every other page on the site. The component emits its own
              BreadcrumbList schema and prepends Home automatically.
              The intermediate crumb is now the topic hub (e.g.
              "Pregnancy & Baby Hub") instead of a generic "Articles" crumb.
              This passes topical link equity through the breadcrumb structure
              and helps Google associate each post with its hub cluster. */}
          <Breadcrumbs
            className="mb-6"
            items={[
              { label: hub.label, href: hub.href },
              { label: title },
            ]}
          />

          {/* This said "Back to Research" while the breadcrumb immediately
              above it said "Home > Microplastic-Free Products >" — two answers
              to "where did I come from", 24px apart. It now mirrors the
              breadcrumb's parent, so both point to the same place. */}
          <Link
            href={hub.href}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-forest mb-8 transition-colors duration-200 min-h-[44px] py-2"
          >
            <FaArrowLeft className="w-4 h-4" aria-hidden="true" />
            Back to {hub.label}
          </Link>

          <article className="prose prose-base sm:prose-lg max-w-none prose-headings:scroll-mt-24 prose-img:rounded-xl">
            <h1 className="font-display text-[clamp(1.75rem,5vw,2.5rem)] font-bold text-ink mb-4 md:mb-6 leading-[1.12]">
              {title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 md:gap-6 text-gray-600 mb-3 text-sm md:text-base">
              {resolvedAuthor ? (
                <Link
                  href={`/authors/${resolvedAuthor.slug}`}
                  className="group flex items-center gap-3 hover:text-forest transition-colors"
                >
                  {/* No aria-label here on purpose. It used to read
                      `About ${name}`, which omitted the visible job title
                      and so failed WCAG 2.5.3 "Label in Name" — the
                      accessible name must contain the visible text. Letting
                      the name come from the link's own content gives
                      "MicroPlastics Research Desk Editorial team", which
                      contains everything a speech-input user can see. */}
                  <span className="relative inline-block w-9 h-9 rounded-full overflow-hidden border border-black/5 shrink-0">
                    {/* Decorative: the author name is shown right next to
                        the avatar, so the link is already named by its text.
                        Empty alt avoids duplicate screen-reader announcement
                        and double-name on copy/paste. */}
                    <Image
                      src={resolvedAuthor.image}
                      alt=""
                      fill
                      sizes="36px"
                      className="object-cover"
                    />
                  </span>
                  <span className="leading-tight">
                    <span className="block font-semibold text-gray-800 group-hover:text-forest">
                      {resolvedAuthor.name}
                    </span>
                    <span className="block text-xs text-gray-500">{resolvedAuthor.jobTitle}</span>
                  </span>
                </Link>
              ) : (
                <div className="flex items-center gap-2">
                  <FaUserCircle className="w-5 h-5" />
                  <span>{author}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <FaCalendarAlt className="w-5 h-5" />
                <span>{date}</span>
              </div>
              <div className="flex items-center gap-2">
                <FaClock className="w-5 h-5" />
                <span>{readTime}</span>
              </div>
            </div>

            {/* Visible "Last reviewed" + sources / corrections trust strip.
                Per June 2026 Google quality-rater guidance, YMYL pages
                showing a verifiable last-review date + a visible link to
                the editorial methodology get a measurable E-E-A-T lift.
                The post's modifiedISO (or publishedISO fallback) is the
                last-review date; the Research Desk profile and corrections
                contact route give Google quality raters something
                inspectable.

                We render only when we have a post.modifiedISO, falls
                back gracefully on the (rare) post without it. */}
            {reviewedISO && (
              <p className="not-prose mb-8 text-xs text-gray-500 leading-relaxed">
                <span className="font-semibold text-gray-700">Last reviewed</span>:{' '}
                <time dateTime={reviewedISO}>
                  {/* timeZone: 'UTC' is load-bearing, do not drop it.
                      reviewedISO is a bare date ("2026-08-19"), which
                      Date parses as UTC midnight. Without an explicit
                      timeZone, toLocaleDateString renders in whatever zone
                      the renderer is in — so Vercel (UTC) prerendered
                      "August 19, 2026" while every reader west of UTC
                      hydrated to "August 18, 2026". That was both a visibly
                      wrong date for most of our US audience and the source
                      of the React #425 hydration mismatch on all ~277 posts. */}
                  {new Date(reviewedISO).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    timeZone: 'UTC',
                  })}
                </time>
                {' '}by{' '}
                <Link
                  href={`/authors/${resolvedAuthor?.slug ?? 'microplastics-research-desk'}`}
                  className="font-semibold text-gray-700 underline hover:text-forest"
                >
                  the MicroPlastics Research Desk
                </Link>
                {'. '}
                <Link
                  href="/contact"
                  className="underline hover:text-forest"
                >
                  Submit a correction
                </Link>
                {' or see our '}
                <Link
                  href="/about"
                  className="underline hover:text-forest"
                >
                  editorial standards
                </Link>
                .
              </p>
            )}

            {/* Above the fold: the ANSWER, not an ad.
                This slot used to hold HeroAppCTA, a dismissible App Store
                banner that ran 2,975 impressions → 11 clicks (0.37%) → 54
                dismissals in the 28 days to 2026-07-13. Readers closed it five
                times more often than they clicked it, which is what happens
                when you interrupt someone with an install ask before answering
                the question they arrived with.
                It now renders the article's own BlogTLDR verdict (hoisted out
                of the body by extractTLDR) with a single quiet app line beneath
                it. The reader gets what they searched for in the first
                viewport; the ask comes after we've earned it. See HeroAnswer. */}
            {heroTLDR && (
              <HeroAnswer topic={topic} postSlug={slug}>
                {heroTLDR}
              </HeroAnswer>
            )}

            {ARTICLE_PORTRAITS[slug] ? (
              <ArticlePortrait portrait={ARTICLE_PORTRAITS[slug]} />
            ) : resolvedHero && (
              <figure className="mb-8">
              <div className="relative w-full aspect-[16/10] sm:aspect-[16/9] md:h-[400px] md:aspect-auto rounded-xl overflow-hidden">
                <Image
                  src={resolvedHero}
                  overrideSrc={resolvedHero}
                  alt={heroImageAlt || title}
                  fill
                  className="object-cover bg-forest/10"
                  loading="eager"
                  fetchPriority="high"
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 90vw, 896px"
                />
              </div>
              {heroImageCaption && <figcaption className="mt-2 text-xs leading-relaxed text-gray-500">{heroImageCaption}</figcaption>}
              </figure>
            )}

            {toc.length > 0 && (
              <nav
                aria-label="Table of contents"
                className="not-prose mb-10 border-y border-ink/10 py-5"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-forest mb-3">
                  On this page
                </p>
                <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5">
                  {toc.map((item, i) => (
                    <li key={item.id} className="flex gap-2.5 text-sm leading-snug">
                      {/* forest/90, not /50: the old value scored 2.26:1 on
                          white against the 4.5:1 minimum. /90 is the lightest
                          step that passes, at 5.15:1. */}
                      <span aria-hidden="true" className="shrink-0 whitespace-nowrap font-mono text-xs text-forest/90 pt-0.5 tabular-nums">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <Link
                        href={`#${item.id}`}
                        prefetch={false}
                        className="text-gray-600 hover:text-forest transition-colors"
                      >
                        {item.text}
                      </Link>
                    </li>
                  ))}
                </ol>
              </nav>
            )}

            <PreferredSourcesArticleCard postSlug={slug} postTopic={topic} />

            <div className="space-y-6 text-gray-700 leading-relaxed">
              {injectResearchSupport(
                injectReadNext(injectMidArticleCTA(tocBody, topic, slug), readNext, readNextHub, slug, topic),
                slug,
                topic
              )}
            </div>

            {/* Article-specific task with a recorded example and app/web
                paths, or the shared topic CTA when no task is authored. */}
            {firstScanPrompt ? (
              <FirstScanCallout prompt={firstScanPrompt} postSlug={slug} postTopic={topic} />
            ) : (
              <div className="mt-8 p-6 md:p-8 bg-gradient-to-br from-[#1E6B47]/10 to-[#1E6B47]/5 rounded-xl border border-forest/20">
                <h3 className="text-xl font-bold text-gray-800 mb-3">
                  {bottomCta.headline}
                </h3>
                <p className="text-gray-600 mb-4">
                  {bottomCta.sub}
                </p>
                <AppStoreLink
                  source={bottomCta.source}
                  ctaPosition="bottom"
                  trackingParams={{
                    post_slug: slug,
                    post_topic: topic,
                    page_type: 'blog',
                    cta_copy: 'Download on the App Store',
                    organic_query_family: topic,
                    app_store_campaign_token: bottomCta.source,
                  }}
                  className="inline-flex items-center bg-ink text-cream px-6 py-3 rounded-control hover:bg-ink/90 transition-[color,background-color,border-color,text-decoration-color] duration-200 pressable"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.08 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                  </svg>
                  Download on the App Store
                </AppStoreLink>
                <AppCtaTrustRow className="mt-4" />
                {/* Social proof — the blog CTAs were the only install asks on
                    the site with zero human evidence (the homepage and
                    scanner page both carry reviews). One verbatim App Store
                    quote + the rating closes that gap. */}
                <p className="mt-4 pt-4 border-t border-forest/20 text-sm text-gray-600 italic">
                  &ldquo;Really cool to scan stuff around the kitchen and see what&apos;s actually in it. The swaps it suggests are realistic.&rdquo;
                  <span className="not-italic text-gray-500"> App Store review · 5.0★</span>
                </p>
              </div>
            )}

            {/* Android catch. The install ask above is iOS-only, but a large
                share of mobile readers are on Android and can't act on it. The
                Android box with a button opens the name + email waitlist modal
                (the home-page pattern), turning that otherwise-dead intent into
                a captured signup on every article. Wrapped in not-prose so the
                article's prose typography doesn't restyle the card. Surface is
                topic-scoped so blog-sourced signups attribute separately. */}
            <div className="not-prose">
              <AndroidWaitlistCard surface={`blog_article_${topic}`} />
            </div>

            {/* Offer a useful web step alongside the app destination. */}
            {topic === 'cosmetics' ? (
              <IngredientCheckerPromo postSlug={slug} postTopic={topic} />
            ) : (
              <RiskQuizInlineNudge
                source={`blog_bottom_nudge_${topic}`}
                text="Prefer to start on the web?"
              />
            )}

            {/* ShareButtons — the viral mechanism the site was missing. Mounts
                between the bottom App Store CTA and Related Research so readers
                who got value have a one-tap path to send the post to a friend.
                Yuka's whole moat is word-of-mouth shares, adding this turns
                every blog reader into a potential distribution node. Custom
                share text per post pulls the article title so the shared link
                isn't a bare URL. */}
            <ShareButtons
              url={`https://microplasticsapp.com/blog/${slug}`}
              shareText={`Read: ${title}`}
              source={`blog_share_${topic}`}
              surface="blog_bottom"
            />

            {/* Topic-aware secondary pillar — moved BELOW the install ask +
                share row (July 2026 funnel audit): it used to be the first
                thing after the body, diverting peak-intent readers to hub
                pages before they ever saw the app CTA. Sources its hub from
                the unified TOPIC_HUB map so the aside, the breadcrumb, and
                the bottom CTA all agree on what hub this article belongs to. */}
            <aside
              aria-label="Microplastics Guide"
              className="mt-12 rounded-xl border border-black/10 bg-white p-5 md:p-6 shadow-sm"
            >
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                New here?
              </p>
              <p className="text-gray-700 leading-relaxed">
                Start with the{' '}
                <Link
                  href="/microplastics-guide"
                  className="font-semibold text-forest underline hover:text-deepForest"
                  onClick={() => track('topic_hub_click', { cta_surface: 'blog_new_here_aside', destination: '/microplastics-guide', post_slug: slug, post_topic: topic })}
                >
                  full Microplastics Guide
                </Link>
                {hub.href !== '/microplastics-guide' ? (
                  <>
                    , the{' '}
                    <Link
                      href={hub.href}
                      className="font-semibold text-forest underline hover:text-deepForest"
                      onClick={() => track('topic_hub_click', { cta_surface: 'blog_new_here_aside', destination: hub.href, post_slug: slug, post_topic: topic })}
                    >
                      {hub.label}
                    </Link>
                  </>
                ) : null}
                , or scan a product directly with the{' '}
                <Link
                  href="/microplastics-scanner-app"
                  className="font-semibold text-forest underline hover:text-deepForest"
                  onClick={() => track('topic_hub_click', { cta_surface: 'blog_new_here_aside', destination: '/microplastics-scanner-app', post_slug: slug, post_topic: topic })}
                >
                  MicroPlastics scanner app
                </Link>
                .
              </p>
            </aside>

            {/* Related posts — drives internal link density + keeps users on site.
                Each click is tracked as `related_article_click` so we can see
                which from→to topic pairs convert into deeper sessions.
                Renders 6 cards instead of 3, doubles outbound link density
                from every article and helps lift "Discovered - not indexed"
                pages out of the GSC crawl-budget penalty box. Grid is 2 cols
                on tablet and 3 cols on desktop so 6 fits cleanly. */}
            {/* Curated links into the site's least-crawled articles, rendered
                only on the handful of pages Google fetches most. Separate from
                the automatic ring above on purpose: the ring spreads links
                evenly, which is right for discovery but useless for rescuing a
                specific page that has never been crawled. See editorialLinks.ts
                for the traffic figures behind each source page. */}
            {editorial.length > 0 && (
              <section aria-label="Deeper reading" className="mt-12">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Go deeper</h2>
                <p className="text-gray-600 mb-5">
                  Less-travelled research from our archive that follows directly
                  from this page.
                </p>
                <ul className="space-y-3">
                  {editorial.map((item) => {
                    const post = getPostBySlug(item.slug)
                    if (!post) return null
                    return (
                      <li key={item.slug}>
                        <Link
                          href={`/blog/${item.slug}`}
                          onClick={() =>
                            track('related_article_click', {
                              cta_surface: 'blog_editorial_links',
                              post_slug: slug,
                              post_topic: topic,
                              destination: `/blog/${item.slug}`,
                              destination_topic: post.topic,
                            })
                          }
                          className="group flex flex-col gap-1 rounded-xl border border-black/5 bg-white p-4 hover:border-forest/30 hover:shadow-md transition-[color,background-color,border-color,box-shadow] duration-200"
                        >
                          <span className="font-semibold text-gray-800 group-hover:text-forest transition-colors duration-200">
                            {post.title}
                          </span>
                          <span className="text-sm text-gray-600">{item.reason}</span>
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              </section>
            )}

            {related.length > 0 && (
              <section aria-label="Related guides" className="mt-12">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Related Research</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {related.map((relatedPost) => (
                    <Link
                      key={relatedPost.slug}
                      href={`/blog/${relatedPost.slug}`}
                      onClick={() =>
                        track('related_article_click', {
                          cta_surface: 'blog_related_articles',
                          post_slug: slug,
                          post_topic: topic,
                          destination: `/blog/${relatedPost.slug}`,
                          destination_topic: relatedPost.topic,
                        })
                      }
                      className="group block rounded-xl bg-white border border-black/5 p-5 hover:border-forest/30 hover:shadow-md transition-[color,background-color,border-color,text-decoration-color,box-shadow] duration-200"
                    >
                      <h3 className="font-semibold text-gray-800 group-hover:text-forest transition-colors duration-200 mb-2 line-clamp-2">
                        {relatedPost.title}
                      </h3>
                      <p className="text-sm text-gray-600 line-clamp-3 mb-3">
                        {relatedPost.excerpt}
                      </p>
                      <span className="inline-flex items-center gap-1 text-sm text-forest font-medium">
                        Read more <FaArrowRight className="w-3 h-3" />
                      </span>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </article>
        </div>
      </div>

      {/* Mobile-only sticky App Store CTA. Appears after 25% scroll, dismissable
          for the session. Tracks `sticky_app_cta_click` for surface CTR and
          `app_store_click` as the single key event, avoiding duplicate
          conversions while preserving placement attribution. */}
      <StickyMobileAppCTA
        source="blog_sticky_mobile"
        trackingParams={{
          post_slug: slug,
          post_topic: topic,
          page_type: 'blog',
          cta_copy: 'Get the App',
          organic_query_family: topic,
          app_store_campaign_token: 'blog_sticky_mobile',
        }}
      />
    </div>
  )
}
